import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBreakpoint, useIFrame } from '@ocean/hooks'
import { Mode } from '@ocean/types/dist/appearance'
import { common, controls, modals } from '@ocean/ui'
import ActionsLayout, { ActionsLayoutVariant } from 'layouts/ActionsLayout/ActionsLayout'

const { Spacer } = common
const { Button } = controls
const { Popup } = modals

const I18_PATH = 'communicationIFrameLayer'

enum SupportedOrigins {
  STA = 'STA',
}

enum ActionType {
  ShowMessage = 'ShowMessage',
}

type Action = {
  type: ActionType
  payload?: any
}

type Message = {
  origin: SupportedOrigins
  action: Action
}

type MessageHandlers = Record<ActionType, (payload: any) => void>

type MessagePopupPayload = {
  title: string
  description: string
}

// Common layer for communications with iframes
const CommunicationIFrameLayer: React.FC = ({ children }) => {
  const { t } = useTranslation()
  const isDesktop = useBreakpoint('sm')
  const [isMessagePopupShown, setIsMessagePopupShown] = useState(false)
  const [messagePayload, setMessagePayload] = useState<MessagePopupPayload>()
  useIFrame<Message>({ onGetMessage: handleMessage })

  const messageHandlers: MessageHandlers = {
    [ActionType.ShowMessage]: handleShowMessage,
  }

  function handleMessage(message: Message) {
    const { origin, action } = message
    if (origin === SupportedOrigins.STA) {
      const { type, payload } = action
      const messageHandler = messageHandlers[type]
      messageHandler && messageHandler(payload)
    }
  }

  function handleShowMessage(messagePayload: any) {
    setMessagePayload(messagePayload as MessagePopupPayload)
    setIsMessagePopupShown(true)
  }

  function handleCloseMessage() {
    setIsMessagePopupShown(false)
  }

  function renderMessagePopup() {
    const { title, description = '' } = messagePayload || {}
    return (
      <Popup
        isOpen={isMessagePopupShown}
        title={title}
        width={isDesktop ? '500px' : undefined}
        mode={Mode.Dark}
        onClose={handleCloseMessage}
        dataTest='CommunicationIFrameLayerMessagePopup'
      >
        <>
          <div
            dangerouslySetInnerHTML={{ __html: description }}
            data-test='CommunicationIFrameLayerMessagePopupDescription'
          />
          <Spacer size='large' />
          <ActionsLayout variant={ActionsLayoutVariant.Modal}>
            <Button
              fill
              title={t(`${I18_PATH}.close`)}
              onClick={handleCloseMessage}
              dataTest='CommunicationIFrameLayerMessagePopupCloseButton'
            />
          </ActionsLayout>
        </>
      </Popup>
    )
  }

  return (
    <>
      {children}
      {renderMessagePopup()}
    </>
  )
}

export default CommunicationIFrameLayer
